import DragHandleIcon from "@mui/icons-material/DragHandle";
import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDispatch, useSelector } from "react-redux";
import {
  REORDER_REPORT_OBSERVATIONS,
  REORDER_REPORT_ROOMS,
  REORDER_REPORT_SUB_CONTENT,
  REORDER_REPORT_SECTIONS,
} from "../../../../store/actions";
import { Spinner } from "react-bootstrap";

export default function DocumentNavigator({
  list,
  scrollToRow,
  setScrollToRow,
}) {
  const toc = useSelector((state) => state.report?.toc?.content);
  return (
    <ul className="ul-ir">
      <MainList
        toc={toc}
        list={list}
        scrollToRow={scrollToRow}
        setScrollToRow={setScrollToRow}
      />
    </ul>
  );
}

const getObservationPageCount = (obs) =>
  (obs?.comments?.length > 0 ? 1 : 0) +
  (obs?.tests?.length > 0 ? 1 : 0) +
  (obs?.images?.length > 0 ? 1 : 0);

const getRoomStartPages = (rooms, baseStartPage = 5) => {
  const starts = [];
  let startPage = baseStartPage;
  (rooms || []).forEach((room) => {
    starts.push(startPage);
    const roomPages =
      (room?.observations || []).reduce(
        (a, c) => a + getObservationPageCount(c),
        0
      ) + 1;
    startPage += roomPages;
  });
  return starts;
};

function SortableObservationItem({
  ob,
  index,
  roomIndex,
  page,
  roomObservations,
  selectedObs,
  selectedSection,
  selectKey,
  setSelectedSection,
  setSelectedObs,
  setScrollToRow,
  scrollToRow,
  setScrollTrigger,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `room-${roomIndex}-ob-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    userSelect: "none",
    background: isDragging ? "#CFE7EC" : "transparent",
    cursor: isDragging ? "grabbing" : "pointer",
    borderRadius: "8px",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <li
        className={
          selectedObs === index && selectedSection === selectKey
            ? "section-children-active section-children one-line-ellipsis"
            : "section-children one-line-ellipsis"
        }
        onClick={() => {
          const earlierPages = (roomObservations || [])
            .slice(0, index)
            .reduce((sum, prev) => sum + getObservationPageCount(prev), 0);
          const scrollRow = page + earlierPages;

          setSelectedSection(selectKey);
          setSelectedObs(index);
          setScrollToRow(scrollRow);
          if (scrollToRow === scrollRow) {
            setScrollTrigger((prev) => prev + 1);
          }
        }}
      >
        <span>
          #{ob.code.toUpperCase()} - {ob.location}
        </span>
      </li>
    </div>
  );
}

function ObservationAccordion({
  roomIndex,
  roomStartPage,
  list,
  scrollToRow,
  setScrollToRow,
  selectKey,
  selectedSection,
  setSelectedSection,
}) {
  const room = useSelector(
    (state) => state.report.observations?.data[roomIndex]
  );
  const [page, setPage] = useState(roomStartPage ?? 5);
  const [selectedObs, setSelectedObs] = useState(0);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const dispatch = useDispatch();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (typeof roomStartPage === "number") {
      setPage(roomStartPage);
    }
  }, [roomStartPage]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const observations = room?.observations || [];
      const oldIndex = observations.findIndex(
        (_, i) => `room-${roomIndex}-ob-${i}` === active.id
      );
      const newIndex = observations.findIndex(
        (_, i) => `room-${roomIndex}-ob-${i}` === over.id
      );

      dispatch({
        type: REORDER_REPORT_OBSERVATIONS,
        payload: {
          sourceIndex: oldIndex,
          destinationIndex: newIndex,
          roomIndex,
        },
      });
    }
  };

  useEffect(() => {
    list?.current?.scrollToRow(scrollToRow);
    const id = setTimeout(() => {
      list?.current?.scrollToRow(scrollToRow);
    }, 250);

    return () => clearTimeout(id);
  }, [scrollTrigger]);

  const items =
    room?.observations?.map((ob, i) => `room-${roomIndex}-ob-${i}`) || [];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div style={{ paddingLeft: "50px", borderRadius: "8px" }}>
          {room?.observations?.map((ob, i) => (
            <SortableObservationItem
              key={`room-${roomIndex}-ob-${i}`}
              ob={ob}
              index={i}
              roomIndex={roomIndex}
              page={page}
              roomObservations={room?.observations}
              selectedObs={selectedObs}
              selectedSection={selectedSection}
              selectKey={selectKey}
              setSelectedSection={setSelectedSection}
              setSelectedObs={setSelectedObs}
              setScrollToRow={setScrollToRow}
              scrollToRow={scrollToRow}
              setScrollTrigger={setScrollTrigger}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRoomItem({
  r,
  j,
  isOpen,
  setIsOpen,
  roomStartPage,
  ObservationAccordionProps,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `room-${j}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    userSelect: "none",
    background: isDragging ? "#CFE7EC" : "transparent",
    cursor: isDragging ? "grabbing" : "auto",
    borderRadius: "8px",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <li
        {...attributes}
        {...listeners}
        className={"section-children one-line-ellipsis"}
        onClick={() => {
          setIsOpen((s) => (s === j ? -1 : j));
          if (typeof roomStartPage === "number") {
            ObservationAccordionProps?.setScrollToRow?.(roomStartPage);
          }
        }}
      >
        <DragHandleIcon className="mx-2 cursor-move" />
        <span style={{ paddingLeft: "20px" }}>{r.room_name}</span>
      </li>
      {isOpen === j && (
        <ObservationAccordion
          {...ObservationAccordionProps}
          selectKey={ObservationAccordionProps.selectKeyI + "" + j}
          roomIndex={j}
          roomStartPage={roomStartPage}
        />
      )}
    </div>
  );
}

function RoomsList({ rooms, ObservationAccordionProps }) {
  const [isOpen, setIsOpen] = useState(-1);
  const dispatch = useDispatch();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rooms.findIndex((_, i) => `room-${i}` === active.id);
      const newIndex = rooms.findIndex((_, i) => `room-${i}` === over.id);

      dispatch({
        type: REORDER_REPORT_ROOMS,
        payload: {
          sourceIndex: oldIndex,
          destinationIndex: newIndex,
        },
      });
    }
  };

  const items = rooms?.map((r, j) => `room-${j}`) || [];
  const roomStartPages = getRoomStartPages(rooms);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div style={{ borderRadius: "8px" }}>
          {rooms?.map((r, j) => (
            <SortableRoomItem
              key={`room-${j}`}
              r={r}
              j={j}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              roomStartPage={roomStartPages[j]}
              ObservationAccordionProps={ObservationAccordionProps}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableSubContentItem({
  content,
  j,
  selectedSection,
  setScrollToRow,
  setSelectedSection,
  sectionI,
  draggable,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `subsection-${j}`, disabled: draggable === false });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    userSelect: "none",
    background: isDragging ? "#CFE7EC" : "transparent",
    cursor: isDragging ? "grabbing" : "auto",
    borderRadius: "8px",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <li
        id={content.page}
        className={
          selectedSection === sectionI + "" + j
            ? "section-children-active section-children one-line-ellipsis"
            : "section-children one-line-ellipsis"
        }
        onClick={() => {
          setScrollToRow(content.page);
          setSelectedSection(sectionI + "" + j);
        }}
      >
        <DragHandleIcon className="mx-2 cursor-move" />
        <span style={{ paddingLeft: "20px" }}>{content.label}</span>
      </li>
    </div>
  );
}

function SubContentList(props) {
  const {
    subContent,
    selectedSection,
    setScrollToRow,
    setSelectedSection,
    sectionI,
  } = props;
  const dispatch = useDispatch();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = subContent.findIndex(
        (_, i) => `subsection-${i}` === active.id
      );
      const newIndex = subContent.findIndex(
        (_, i) => `subsection-${i}` === over.id
      );

      dispatch({
        type: REORDER_REPORT_SUB_CONTENT,
        payload: {
          sourceIndex: oldIndex,
          destinationIndex: newIndex,
          contentIndex: sectionI,
        },
      });
    }
  };

  const items = subContent?.map((content, j) => `subsection-${j}`) || [];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div style={{ borderRadius: "8px" }}>
          {subContent?.map((content, j) => (
            <SortableSubContentItem
              key={`subsection-${j}`}
              content={content}
              j={j}
              selectedSection={selectedSection}
              setScrollToRow={setScrollToRow}
              setSelectedSection={setSelectedSection}
              sectionI={sectionI}
              draggable={props.draggable}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableSectionItem({
  section,
  i,
  observationRooms,
  list,
  scrollToRow,
  setScrollToRow,
  setSelectedSection,
  selectedSection,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `main-list-${i}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    userSelect: "none",
    background: isDragging ? "#fff" : "transparent",
    cursor: isDragging ? "grabbing" : "auto",
    borderRadius: "8px",
    border: "1px solid #fff",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <li className="section-accordion">
        <DragHandleIcon className="mx-2 cursor-move" />
        <span className="one-line-ellipsis">{section?.title}</span>
      </li>
      {section?.report_key === "observations" ? (
        observationRooms === undefined ? (
          <div className="center-div">
            <Spinner animation="border" variant="success" />
          </div>
        ) : (
          <RoomsList
            rooms={observationRooms}
            ObservationAccordionProps={{
              list,
              scrollToRow,
              setScrollToRow,
              setSelectedSection,
              selectedSection,
              selectKeyI: i,
            }}
          />
        )
      ) : (
        <SubContentList
          subContent={section.sub_content}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          setScrollToRow={setScrollToRow}
          sectionI={i}
        />
      )}
    </div>
  );
}

function MainList(props) {
  const { toc, list, scrollToRow, setScrollToRow } = props;
  const [selectedSection, setSelectedSection] = useState("00");
  const dispatch = useDispatch();
  const observationRooms = useSelector(
    (state) => state.report?.observations?.data
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = toc.findIndex((_, i) => `main-list-${i}` === active.id);
      const newIndex = toc.findIndex((_, i) => `main-list-${i}` === over.id);

      dispatch({
        type: REORDER_REPORT_SECTIONS,
        payload: {
          sourceIndex: oldIndex,
          destinationIndex: newIndex,
        },
      });
    }
  };

  const items = toc?.slice(1).map((section, i) => `main-list-${i + 1}`) || [];

  return (
    <>
      <li className="section-accordion" style={{ cursor: "auto" }}>
        <DragHandleIcon className="mx-2 cursor-move" />
        <span className="one-line-ellipsis">{toc?.[0]?.title}</span>
      </li>
      <SubContentList
        draggable={false}
        subContent={toc?.[0]?.sub_content}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        setScrollToRow={setScrollToRow}
        sectionI={0}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div style={{ borderRadius: "8px" }}>
            {toc?.map((section, i) =>
              i === 0 ? null : (
                <SortableSectionItem
                  key={`main-list-${i}`}
                  section={section}
                  i={i}
                  observationRooms={observationRooms}
                  list={list}
                  scrollToRow={scrollToRow}
                  setScrollToRow={setScrollToRow}
                  setSelectedSection={setSelectedSection}
                  selectedSection={selectedSection}
                />
              )
            )}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
}
